export enum GroupNames {
  ENT_PARTNERS = "Chicago ENT",
  ENT_PARTNERS_SCI = "Specialty Care ENT",
  NIRA = "Central Texas Neurology Consultants",
  NIMBUS = "Nimbus Health",
  UPPERLINE_GA = "Upperline Georgia",
  VALERIE = "Valerie Health",
}

export enum GroupIds {
  ENT_PARTNERS_LIVE = "ed018461-8325-4332-93d5-62c74070188f",
  ENT_PARTNERS_LIVE_SANDBOX = "e333fb87-018e-43bb-863d-d06e84beab20",
  ENT_PARTNERS_SCI_LIVE = "02a9e986-a7bb-4aab-a296-6e959d3128c1",
  ENT_PARTNERS_SCI_LIVE_SANDBOX = "bd2e4fa7-9a32-4370-abbb-19b0a54d87b0",
  HEARTPLACE_LIVE = "eb922daf-4fdc-43b3-88dc-e8ec5f795a7d",
  HEARTPLACE_LIVE_SANDBOX = "1fa70383-e938-4c97-a702-f46651f33832",
  NIRA_LIVE = "9b6978a8-a8fe-4c99-bf5a-a2ce6161248c",
  NIRA_LIVE_SANDBOX = "ac2a1f74-487d-41a0-b212-1490d94f728e",
  UPPERLINE_GA_LIVE = "8bab885a-339d-46af-ae77-6491b32125e5",
  UPPERLINE_GA_LIVE_SANDBOX = "4f5c0cf8-5b73-4a75-88d6-4121648d7279",
  VALERIE_HEALTH_DEV = "a8519747-7191-4142-8ba2-a37a77d329af",
  VALERIE_HEALTH_LIVE_SANDBOX = "483052a4-72da-4792-94b3-9c9f8048f0ea",
  BALANCE_HEALTH_LIVE = "f9125d70-411a-414e-9b80-5465152ed38e",
  BALANCE_HEALTH_LIVE_SANDBOX = "e85d79cb-1402-4e45-8b60-9b43ae8c3845",
  USHV_PIMA_LIVE = "8be9abd2-92d3-4f3e-8268-5e57025f8295",
  USHV_PIMA_LIVE_SANDBOX = "941783e9-ff92-4f48-99c2-439088df8a92",
  NIMBUS_LIVE = "c37e8d73-3dbf-4b9a-9f3e-138b611de24c",
  NIMBUS_LIVE_SANDBOX = "e03ec760-3cc1-4603-b65f-f9b862ed5b71",
}

export const DEMO_AGENT_V1 = "17737404006";
export const DEMO_AGENT_V2 = "17737404002";
export const STAGING_NUMBER = "12402124097";
export const VOICE_STAGING_TWILIO_NUMBER = "+16303946071";
export const CONVENIENT_MD_TEST_LINE = "+15512506773";
export const CONVENIENT_MD_TRANSFER_LINE = "+16039120106";

// Mock test lines (VAL-1882 / VAL-1883): route to the *_LIVE_SANDBOX groups, which run the real
// entp / entp-sci / upperline-ga workflows against the Supabase mock EHR/scheduler (no real EHR side effects).
// Alert suppression for these is by GROUP (see MOCK_TEST_GROUP_IDS), not by number.
export const ENTP_MOCK_LINE = "+18776744317";
export const ENTP_SCI_MOCK_LINE = "+12608234006";
export const UPPERLINE_GA_MOCK_LINE = "+16507064884";

export const TEST_ENV_NUMBERS = [
  DEMO_AGENT_V1,
  DEMO_AGENT_V2,
  STAGING_NUMBER,
  VOICE_STAGING_TWILIO_NUMBER,
  CONVENIENT_MD_TEST_LINE,
];

// Groups used purely for testing against the Supabase mock EHR/scheduler (VAL-1820/1882/1883). Calls
// to these are excluded from transcript ingestion / #voice-alerts regardless of the dialed number —
// suppression by org, not by line, so it holds for any inbound path (mock number, staging, etc.).
export const MOCK_TEST_GROUP_IDS: GroupIds[] = [
  GroupIds.VALERIE_HEALTH_LIVE_SANDBOX,
  GroupIds.ENT_PARTNERS_LIVE_SANDBOX,
  GroupIds.ENT_PARTNERS_SCI_LIVE_SANDBOX,
  GroupIds.UPPERLINE_GA_LIVE_SANDBOX,
];

export const SAMPLE_ALLOW_LIST = [
  "+19255889354",
  "+18474173869",
  "+15122645802",
  "+18479702471",
  "+19145526753",
  "+13365546880",
  "+16696009800",
];

export const CTNC_NEW_PATIENT_LINE = "+15128773634";
export const CTNC_NEW_PATIENT_TRANSFER_LINE = "+15127661164";
export const CTNC_EXISTING_PATIENT_LINE = "+15126018189";
export const CTNC_EXISTING_PATIENT_TRANSFER_LINE = "+15127667966";

export const NIMBUS_SCW_LINE = "+16233215578";
export const NIMBUS_ROCK_HILL_LINE = "+18032675105";
export const NIMBUS_MACON_LINE = "+14783098444";

export const UPPERLINE_GA_LINE = "+17069171729";
export const UPPERLINE_GA_TRANSFER_LINE = "+17069552843";

export const ENTP_LINE = "+17737419917";
export const ENTP_SMS_TRANSFER_LINE = "+17732965500";
export const ENTP_TRANSFER_LINE_MEDIUM_PRIORITY = "+17735963593";
export const ENTP_TRANSFER_LINE_HIGH_PRIORITY = "+17735963596";
export const ENTP_TRANSFER_LINE_STANDARD_PRIORITY = "+17735963597";
export const ENTP_TRANSFER_LINE_NOTIFY_MD = "+18002080203";
export const ENTP_TRANSFER_LINE_BILLING = "+16128878446";

export const ENTP_SCI_LINE = "+17086225761";
export const ENTP_SCI_TRANSFER_LINE = "+16513775275";

// Inbound-line registry. Workflow configs reference these by name (e.g.
// "CTNC_NEW_PATIENT_LINE") instead of hardcoding phone numbers; the loader
// resolves the name to the underlying number at parse time.
export const INBOUND_LINES = {
  CTNC_NEW_PATIENT_LINE,
  CTNC_EXISTING_PATIENT_LINE,
  UPPERLINE_GA_LINE,
  ENTP_LINE,
  ENTP_SCI_LINE,
  STAGING_NUMBER,
  VOICE_STAGING_TWILIO_NUMBER,
  CONVENIENT_MD_TEST_LINE,
  DEMO_AGENT_V1,
  DEMO_AGENT_V2,
} as const;

export type InboundLineName = keyof typeof INBOUND_LINES;

export const INBOUND_NUMBER_TO_GROUP_ID: Record<string, GroupIds> = {
  [CTNC_NEW_PATIENT_LINE]: GroupIds.NIRA_LIVE,
  [CTNC_EXISTING_PATIENT_LINE]: GroupIds.NIRA_LIVE,
  [STAGING_NUMBER]: GroupIds.VALERIE_HEALTH_LIVE_SANDBOX,
  [VOICE_STAGING_TWILIO_NUMBER]: GroupIds.VALERIE_HEALTH_LIVE_SANDBOX,
  [CONVENIENT_MD_TEST_LINE]: GroupIds.VALERIE_HEALTH_LIVE_SANDBOX,
  [DEMO_AGENT_V1]: GroupIds.VALERIE_HEALTH_DEV,
  [DEMO_AGENT_V2]: GroupIds.VALERIE_HEALTH_DEV,
  [UPPERLINE_GA_LINE]: GroupIds.UPPERLINE_GA_LIVE,
  [ENTP_LINE]: GroupIds.ENT_PARTNERS_LIVE,
  [ENTP_SCI_LINE]: GroupIds.ENT_PARTNERS_SCI_LIVE,
  [ENTP_MOCK_LINE]: GroupIds.ENT_PARTNERS_LIVE_SANDBOX,
  [ENTP_SCI_MOCK_LINE]: GroupIds.ENT_PARTNERS_SCI_LIVE_SANDBOX,
  [UPPERLINE_GA_MOCK_LINE]: GroupIds.UPPERLINE_GA_LIVE_SANDBOX,
};

// A mock test line borrows its live counterpart's inbound-line config (enabled intents), so the mock
// inherits prod and we don't duplicate — or risk diverging — line config in workflows.json. The mock
// groups already resolve to the live config dir (GROUP_CONFIG_DIR_BY_GROUP_ID), so the only remaining
// per-line thing is the enabled-intents lookup; generateWorkflowPrompts resolves it via this alias.
export const MOCK_LINE_TO_LIVE_LINE: Record<string, string> = {
  [ENTP_MOCK_LINE]: ENTP_LINE,
  [ENTP_SCI_MOCK_LINE]: ENTP_SCI_LINE,
  [UPPERLINE_GA_MOCK_LINE]: UPPERLINE_GA_LINE,
};

export const GROUP_DISPLAY_NAMES: Record<string, string> = {
  [GroupIds.VALERIE_HEALTH_DEV]: GroupNames.VALERIE,
  [GroupIds.VALERIE_HEALTH_LIVE_SANDBOX]: GroupNames.VALERIE,
  [GroupIds.NIRA_LIVE]: GroupNames.NIRA,
  [GroupIds.NIRA_LIVE_SANDBOX]: GroupNames.NIRA,
  [GroupIds.NIMBUS_LIVE]: GroupNames.NIMBUS,
  [GroupIds.NIMBUS_LIVE_SANDBOX]: GroupNames.NIMBUS,
  [GroupIds.UPPERLINE_GA_LIVE]: GroupNames.UPPERLINE_GA,
  [GroupIds.UPPERLINE_GA_LIVE_SANDBOX]: GroupNames.UPPERLINE_GA,
  [GroupIds.ENT_PARTNERS_LIVE_SANDBOX]: GroupNames.ENT_PARTNERS,
  [GroupIds.ENT_PARTNERS_LIVE]: GroupNames.ENT_PARTNERS,
  [GroupIds.ENT_PARTNERS_SCI_LIVE_SANDBOX]: GroupNames.ENT_PARTNERS_SCI,
  [GroupIds.ENT_PARTNERS_SCI_LIVE]: GroupNames.ENT_PARTNERS_SCI,
};

// One-line functional description injected into the legacy agent's opening greeting, right after
// the clinic introduction. Mirrors the slow-thinker `presentation.openingDescription` so both
// runtimes greet callers identically. Optional per group — groups without an entry keep the plain
// greeting.
export const GROUP_OPENING_DESCRIPTIONS: Partial<Record<string, string>> = {
  [GroupIds.UPPERLINE_GA_LIVE]: "I can help with scheduling and general FAQs",
  [GroupIds.UPPERLINE_GA_LIVE_SANDBOX]: "I can help with scheduling and general FAQs",
};

export const INBOUND_NUMBER_TO_LOCATION_EHR_ID: Record<string, string> = {
  [CTNC_NEW_PATIENT_LINE]: "1",
  [CTNC_EXISTING_PATIENT_LINE]: "1",
  [STAGING_NUMBER]: "150",
  // [UPPERLINE_GA_LINE]: "<upperline-ga-department-ehr-id>",
  // [ENTP_LINE]: "<entp-sandbox-location-ehr-id>",
};

export function getLocationEhrIdForNumber(calledNumber: string | null | undefined): string | null {
  if (!calledNumber) return null;

  return INBOUND_NUMBER_TO_LOCATION_EHR_ID[calledNumber] ?? null;
}
